import { useEffect, useRef, useState } from 'react';
import type { AdjustmentLayer, EditorDocument } from '../core/model';
import { Canvas2DRenderer } from '../render/renderer';

interface AdjustmentPreviewProps {
  document: EditorDocument;
  layer: AdjustmentLayer;
  targeted?: boolean;
  onTargetStart?: (input: number) => void;
  onTargetDelta?: (delta: number) => void;
  onTargetEnd?: (cancelled: boolean) => void;
}

const renderer = new Canvas2DRenderer();

export function AdjustmentPreview({ document, layer, targeted = false, onTargetStart, onTargetDelta, onTargetEnd }: AdjustmentPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gesture = useRef<{ pointerId: number; startY: number } | null>(null);
  const [preview, setPreview] = useState<{ width: number; height: number; pixels: Uint8ClampedArray } | null>(null);
  const [working, setWorking] = useState(true);
  const adjustmentRevision = JSON.stringify(layer.adjustment);

  useEffect(() => {
    setWorking(true);
    const timeout = window.setTimeout(() => {
      try {
        const scale = Math.min(1, 240 / document.width, 112 / document.height);
        // Render through the authoritative stack so clipping, masks, groups, and layers above the adjustment agree with the canvas.
        const base = renderer.flatten(document, scale);
        const context = base.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('Preview canvas unavailable.');
        const image = context.getImageData(0, 0, base.width, base.height);
        setPreview({ width: image.width, height: image.height, pixels: new Uint8ClampedArray(image.data) });
        setWorking(false);
      } catch {
        setWorking(false);
      }
    }, 100);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [document, layer.id, adjustmentRevision, layer.opacity]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !preview) return;
    canvas.width = preview.width;
    canvas.height = preview.height;
    canvas.getContext('2d')?.putImageData(new ImageData(preview.pixels, preview.width, preview.height), 0, 0);
  }, [preview]);

  return <div className={`adjustment-preview ${working ? 'working' : ''} ${targeted ? 'targeted' : ''}`}>
    <canvas ref={canvasRef} onPointerDown={(event) => {
      const canvas = canvasRef.current;
      if (!targeted || !canvas || !preview) return;
      const bounds = canvas.getBoundingClientRect();
      const x = Math.max(0, Math.min(preview.width - 1, Math.floor((event.clientX - bounds.left) / Math.max(1, bounds.width) * preview.width)));
      const y = Math.max(0, Math.min(preview.height - 1, Math.floor((event.clientY - bounds.top) / Math.max(1, bounds.height) * preview.height)));
      const offset = (y * preview.width + x) * 4;
      const input = ((preview.pixels[offset] ?? 0) * 0.2126 + (preview.pixels[offset + 1] ?? 0) * 0.7152 + (preview.pixels[offset + 2] ?? 0) * 0.0722) / 255;
      gesture.current = { pointerId: event.pointerId, startY: event.clientY };
      event.currentTarget.setPointerCapture(event.pointerId);
      onTargetStart?.(input);
    }} onPointerMove={(event) => {
      if (gesture.current?.pointerId !== event.pointerId) return;
      onTargetDelta?.((gesture.current.startY - event.clientY) / 100);
    }} onPointerUp={(event) => {
      if (gesture.current?.pointerId !== event.pointerId) return;
      gesture.current = null;
      onTargetEnd?.(false);
    }} onPointerCancel={(event) => {
      if (gesture.current?.pointerId !== event.pointerId) return;
      gesture.current = null;
      onTargetEnd?.(true);
    }} />
    <span>{working ? 'Updating stack preview…' : targeted ? 'Sample a tone · drag vertically' : 'Live compositor preview'}</span>
  </div>;
}
